import { Mail, Inbox, Send, Calendar, Users, Search, Sparkles, Phone, Globe, Building2, Check } from "lucide-react";
import avatarSarah from "@/assets/avatar-sarah.jpg";

/**
 * Realistic Outlook window mockup with the Email2Contact add-in side panel.
 * Pure component — no images. Crisp at any size.
 *
 * Responsive behaviour:
 *  - <sm  (mobile): sidebar hidden, single-column stack (email pane → add-in pane)
 *  - sm–lg (tablet): sidebar hidden, 2-column (email + slimmer add-in pane)
 *  - lg+  (desktop): full 3-column layout
 */
export const OutlookMockup = () => {
  return (
    <div className="relative w-full">
      {/* Window frame */}
      <div className="relative rounded-xl bg-card border border-border/80 shadow-fluent-64 overflow-hidden">
        {/* Title bar */}
        <div className="flex items-center justify-between px-3 sm:px-4 py-2 sm:py-2.5 bg-surface-2 border-b border-border/60">
          <div className="flex items-center gap-2">
            <span className="w-2.5 h-2.5 rounded-full bg-[#ff5f57]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#febc2e]" />
            <span className="w-2.5 h-2.5 rounded-full bg-[#28c840]" />
          </div>
          <div className="flex items-center gap-1.5 text-[10px] sm:text-[11px] font-medium text-muted-foreground font-mono">
            <Mail className="w-3 h-3" />
            <span>Outlook — Inbox</span>
          </div>
          <div className="w-12" />
        </div>

        {/* Ribbon */}
        <div className="flex items-center gap-3 sm:gap-4 px-3 sm:px-4 py-2 bg-surface-1 border-b border-border/50 text-[10px] sm:text-[11px] text-muted-foreground">
          <span className="font-semibold text-foreground">Home</span>
          <span className="hidden xs:inline">View</span>
          <span className="hidden xs:inline">Help</span>
          <div className="ml-auto flex items-center gap-1.5 px-2 py-1 rounded bg-primary/10 text-primary font-medium">
            <Sparkles className="w-3 h-3" />
            <span>Email2Contact</span>
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-[1fr_260px] lg:grid-cols-[44px_1fr_320px] sm:min-h-[360px] lg:min-h-[380px]">
          {/* Sidebar — desktop only */}
          <div className="hidden lg:flex bg-surface-2 border-r border-border/50 flex-col items-center py-3 gap-3 text-muted-foreground">
            <Inbox className="w-4 h-4 text-primary" />
            <Send className="w-4 h-4" />
            <Calendar className="w-4 h-4" />
            <Users className="w-4 h-4" />
          </div>

          {/* Email reading pane */}
          <div className="bg-card p-4 sm:p-5 border-b sm:border-b-0 sm:border-r border-border/50">
            <div className="flex items-center justify-between mb-3 sm:mb-4">
              <div className="flex items-center gap-2 px-2 py-1 rounded bg-surface-2 text-[11px] text-muted-foreground">
                <Search className="w-3 h-3" />
                <span>Search</span>
              </div>
            </div>

            <div className="space-y-2">
              <h3 className="text-sm font-semibold text-foreground leading-snug">
                Re: Partnership proposal — Q2 collaboration
              </h3>
              <div className="flex items-center gap-2 text-[11px] text-muted-foreground">
                <img
                  src={avatarSarah}
                  alt="Sarah Mitchell"
                  loading="lazy"
                  width={20}
                  height={20}
                  className="w-5 h-5 rounded-full object-cover"
                />
                <span className="font-medium text-foreground">Sarah Mitchell</span>
                <span>·</span>
                <span>10:24 AM</span>
              </div>
            </div>

            <div className="mt-4 space-y-2 text-[11px] text-muted-foreground leading-relaxed">
              <p>Hi team,</p>
              <p className="hidden sm:block">Thanks for the call yesterday. Looping in our ops lead so we can move forward on the Q2 plan.</p>
              <p className="sm:hidden">Thanks for the call yesterday.</p>
              <p>Best regards,</p>
            </div>

            {/* Signature block (highlighted) */}
            <div className="mt-3 p-3 rounded-md border border-dashed border-primary/40 bg-primary/[0.04] relative">
              <span className="absolute -top-2 left-3 px-1.5 bg-card text-[9px] font-mono uppercase tracking-wider text-primary">
                Signature detected
              </span>
              <div className="text-[11px] space-y-0.5">
                <div className="font-semibold text-foreground">Sarah Mitchell</div>
                <div className="text-muted-foreground">VP of Operations · Northwind Labs</div>
                <div className="text-muted-foreground break-all">+1 (415) 555-0142 · sarah@northwind.io</div>
                <div className="text-muted-foreground">northwind.io</div>
              </div>
            </div>
          </div>

          {/* Add-in panel */}
          <div className="bg-surface-1 p-4 flex flex-col">
            <div className="flex items-center gap-2 mb-3">
              <div className="w-6 h-6 rounded bg-primary grid place-items-center">
                <Sparkles className="w-3.5 h-3.5 text-primary-foreground" />
              </div>
              <div>
                <div className="text-[11px] font-semibold leading-none">Email2Contact</div>
                <div className="text-[9px] text-muted-foreground font-mono mt-0.5">EXTRACTING…</div>
              </div>
              <div className="ml-auto text-[10px] font-mono text-muted-foreground">
                42 credits
              </div>
            </div>

            {/* Extracted fields */}
            <div className="space-y-2.5 flex-1">
              <FieldRow icon={Users} label="NAME" value="Sarah Mitchell" delay={0} />
              <FieldRow icon={Building2} label="COMPANY" value="Northwind Labs" delay={120} />
              <FieldRow icon={Phone} label="PHONE" value="+1 (415) 555-0142" delay={240} />
              <FieldRow icon={Mail} label="EMAIL" value="sarah@northwind.io" delay={360} />
              <FieldRow icon={Globe} label="WEBSITE" value="northwind.io" delay={480} />
            </div>

            <button className="mt-4 w-full py-2 rounded-md bg-primary text-primary-foreground text-[11px] font-semibold flex items-center justify-center gap-1.5 shadow-fluent-2 hover:shadow-fluent-8 transition-shadow">
              <Check className="w-3 h-3" />
              Save to Outlook Contacts
            </button>
          </div>
        </div>
      </div>

      {/* Floating accent: completion badge */}
      <div className="hidden md:flex absolute -bottom-4 -right-4 items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border shadow-fluent-16">
        <div className="w-7 h-7 rounded-full bg-success/10 grid place-items-center">
          <Check className="w-3.5 h-3.5 text-success" />
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase text-muted-foreground tracking-wider">Saved</div>
          <div className="text-xs font-semibold">in 1.4s</div>
        </div>
      </div>

      {/* Floating accent: credit badge */}
      <div className="hidden md:flex absolute -top-4 -left-4 items-center gap-2 px-3 py-2 rounded-lg bg-card border border-border shadow-fluent-16">
        <div className="w-7 h-7 rounded-full bg-primary/10 grid place-items-center">
          <Sparkles className="w-3.5 h-3.5 text-primary" />
        </div>
        <div>
          <div className="text-[10px] font-mono uppercase text-muted-foreground tracking-wider">AI Engine</div>
          <div className="text-xs font-semibold">EU · ephemeral</div>
        </div>
      </div>
    </div>
  );
};

const FieldRow = ({
  icon: Icon,
  label,
  value,
  delay,
}: {
  icon: typeof Users;
  label: string;
  value: string;
  delay: number;
}) => (
  <div
    className="flex items-start gap-2 p-2 rounded-md bg-card border border-border/60 animate-fade-in-up opacity-0"
    style={{ animationDelay: `${delay}ms`, animationFillMode: "forwards" }}
  >
    <Icon className="w-3 h-3 text-muted-foreground mt-0.5 flex-shrink-0" />
    <div className="min-w-0 flex-1">
      <div className="text-[8px] font-mono uppercase tracking-wider text-muted-foreground">{label}</div>
      <div className="text-[11px] font-medium text-foreground truncate">{value}</div>
    </div>
    <Check className="w-3 h-3 text-success flex-shrink-0 mt-0.5" />
  </div>
);
